/**
 * Starter texts for a new contract.
 *
 * Pre-authored bodies a licensor can start a contract from and then publish as
 * their own organisation's v1. They use the standard merge-field tokens
 * ({{licensor_org}}, {{licensee_org}}, {{region}}, {{start_date}},
 * {{end_date}}) that the server fills when an agreement is sent — see
 * HHNodeServer contractService.merge and licenseService._deriveMergeValues.
 *
 * The nursery sublicense is deliberately cultivar- and fee-agnostic. Per-
 * cultivar authorisations, rootstock, quantities, and royalty/program fees
 * are configured when an individual cultivar licence is granted to a
 * sub-nursery under the master sublicense.
 */

import {
  ORCHARD_PLANTING_DEED_BODY,
  ROOTSTOCK_PLANTING_LICENCE_BODY,
} from './orchardLicenceBodies';
import type { ContractTier } from '../../types/contracts';

export interface LicenceStarterTemplate {
  key: string;
  name: string;
  license_tier: ContractTier;
  merge_fields: string[];
  body_richtext: string;
  description: string;
}

const NURSERY_SUBLICENSE_BODY = `# Nursery Sublicense Master Agreement

**Between**

- **The Licensor:** {{licensor_org}} ("the Licensor"), being the holder or authorised regional representative of the Plant Breeder's Rights and associated intellectual property in the cultivars made available through the Licensor's Programs; and
- **The Nursery:** {{licensee_org}} ("the Nursery"), being the propagating and supplying nursery sublicensed under this Agreement.

**Region:** {{region}}
**Term:** {{start_date}} to {{end_date}}

**Platform:** This Agreement is administered through the **FactreeTech** platform ("the Platform"). Disclosures, orders, clearances, audit records, and cultivar-level authorisations contemplated by this Agreement are recorded in the Platform and those records will be taken as the authoritative record.

---

## 1. Purpose and Scope

1.1 This Agreement establishes the Nursery as an **authorised sub-licensed nursery** of the Licensor within the Region for the Term.

1.2 This Agreement **does not, of itself, authorise the Nursery to propagate or supply any particular cultivar**. Each cultivar the Nursery wishes to propagate, grow, or sell must be separately authorised by the issue of a **per-cultivar licence** by the Licensor through the Platform.

1.3 Per-cultivar licences are granted **only by the Licensor holding rights in the relevant Region**, and only to nurseries that have executed this Agreement.

1.4 The commercial terms for each cultivar (including royalties, program fees, authorised quantities, rootstocks, territory within the Region, and duration) are set out in the **Program** to which that cultivar belongs, and in the specific cultivar licence issued to the Nursery. Those terms form part of, and are subject to, this Agreement.

## 2. Conditions to be Met Before Supply of Any Cultivar

2.1 Before the Nursery may propagate, ship, sell, or deliver any cultivar, all of the following must be complete and recorded in the Platform:

  (a) This Agreement has been signed and returned to the Licensor;
  (b) A specific **per-cultivar licence** for that cultivar has been issued by the Licensor to the Nursery through the Platform and is current;
  (c) The applicable **Program Agreement(s)** governing the cultivar have been signed by the Nursery and returned to the Licensor, and the Nursery has been admitted to the relevant Program(s);
  (d) **Non-Propagation Agreements** (or equivalent end-grower undertakings) from each intended recipient of the cultivar have been received by the Licensor and recorded against the order in the Platform;
  (e) Any royalties, program fees, or levies due under the applicable Program have been paid or secured in accordance with the Licensor's payment terms; and
  (f) The Licensor has issued a written **Release to Ship** (including by Platform notification) in respect of the specific trees, order, and recipient.

2.2 Trees may not leave the Nursery's premises without a current Release to Ship for that consignment. A Release to Ship issued for one consignment does not apply to any other consignment.

## 3. Disclosure and Order Transparency

3.1 The Nursery must record and maintain in the Platform, on an ongoing basis, for every cultivar the Nursery is authorised to supply:

  (a) Every order received, including the intended end-grower, property, block, quantities, rootstocks, and proposed delivery date;
  (b) Every sale, delivery, or transfer, including date, quantity, cultivar, rootstock, and recipient details;
  (c) The number of trees currently in propagation, in production, in stock, and in reserve for each cultivar/rootstock combination;
  (d) Any rejections, write-offs, destructions, or disposals, together with the reason.

3.2 The Nursery authorises the Licensor to access, view, export, and audit all such records in the Platform at any time.

3.3 The Nursery must not enter into any binding order, offer, or commitment for a cultivar without that order first being visible to the Licensor in the Platform.

## 4. Sourcing of Propagation Material

4.1 All propagation material (including budwood, cuttings, rootstock, tissue-culture stock, and finished liners) used by the Nursery in connection with sub-licensed cultivars must be:

  (a) **Sourced exclusively** from the Licensor or from a source expressly authorised in writing by the Licensor;
  (b) Recorded in the Platform, identifying source, date received, quantity, and lot/batch reference; and
  (c) Kept traceable through to the individual trees produced.

4.2 The Nursery must **not** collect, propagate from, multiply, or retain any propagation material of a sub-licensed cultivar except in accordance with an authorisation issued by the Licensor for a specific purpose and quantity. Unauthorised propagation is a **material breach** of this Agreement.

4.3 Any surplus propagation material, mother trees, or withdrawn stock must be disposed of in the manner directed by the Licensor and recorded in the Platform.

## 5. True-to-Type and Good-Faith Supply

5.1 The Nursery warrants that every tree supplied under this Agreement will, in good faith and to the best of the Nursery's professional ability, be:

  (a) **True to the identified cultivar and rootstock** — propagated from authorised material of the stated cultivar on the stated rootstock;
  (b) Free from visible pests, diseases, and defects to the standard ordinarily expected of a commercial nursery in the applicable jurisdiction;
  (c) Accurately labelled, recorded, and invoiced; and
  (d) Supplied with the correct accompanying documentation (Non-Propagation Agreement acknowledgement, Program paperwork, delivery docket, and PBR notice).

5.2 The Nursery must not knowingly supply mislabelled, off-type, or substituted plants, and must have reasonable quality-control processes in place to detect and prevent such errors.

## 6. Labelling, Branding, and PBR Communication

6.1 Every tree, bundle, pallet, and consignment of a sub-licensed cultivar must be **clearly labelled** with:

  (a) The registered cultivar name (and trademark name where applicable);
  (b) The rootstock;
  (c) The Program name, where supplied under a Program;
  (d) The applicable PBR symbol/notice (e.g. ®, PBR, Plant Breeder's Rights Protected, UPOV symbol) as specified by the Licensor;
  (e) The Licensor's or Program's approved logo or identifier, where provided; and
  (f) The Nursery's own identifier, for traceability.

6.2 Labels, tags, logos, artwork, and PBR notices will be **supplied or specified by the Licensor**. The Nursery must use them without modification and must not substitute, overprint, or alter them.

6.3 All marketing, catalogue, website, invoice, and quotation references to sub-licensed cultivars must accurately identify them by registered name, acknowledge the PBR/IP status, and comply with any Licensor-issued communication guidelines.

6.4 The Nursery must not use a cultivar name, trademark, logo, or PBR notice on, or in connection with, plants that are not genuine authorised supply of that cultivar.

## 7. Audit Rights

7.1 The Licensor (or its authorised representative) may, on giving the Nursery **not less than 24 hours' notice**, attend the Nursery's premises (including any satellite or contracted-grower site used by the Nursery) to:

  (a) Inspect cultivar stock, mother trees, propagation areas, and record-keeping systems;
  (b) Confirm identity, quantity, health, and rootstock of cultivars in production and stock;
  (c) Review Platform records, invoices, delivery dockets, order books, and Non-Propagation Agreement files;
  (d) Take reasonable photographs and samples for identity verification, including DNA testing at the Licensor's cost.

7.2 The Nursery must cooperate in good faith, provide access, and make relevant records and personnel available. Audits will be conducted so as to minimise disruption to the Nursery's operations.

7.3 If an audit identifies non-compliance, the Licensor may issue a written notice under clause 9.

## 8. Reputation, IP, and Liability

8.1 The Nursery acknowledges that sub-licensed cultivars represent significant investment by the breeder and Licensor, and that their commercial value depends on consistent quality, true-to-type supply, and the integrity of the PBR.

8.2 If the Nursery's acts or omissions (including breach of this Agreement, unauthorised propagation, mislabelling, supply of off-type material, or unauthorised disclosure) **materially damage**:

  (a) The reputation of a cultivar, Program, or the Licensor;
  (b) The validity, enforceability, or commercial value of the PBR or other IP; or
  (c) The commercial relationships of the Licensor with other nurseries, growers, or markets,

the Nursery shall be liable to the Licensor for the resulting loss, damage, cost, and expense (including reasonable legal costs), whether direct or consequential, to the maximum extent permitted by law.

8.3 The Nursery indemnifies the Licensor against any third-party claim arising from the Nursery's breach of this Agreement, its negligent or unauthorised supply, or its misrepresentation of a sub-licensed cultivar.

## 9. Breach, Suspension, and Termination

9.1 If the Licensor reasonably considers that the Nursery has breached any condition of this Agreement or of a per-cultivar licence, the Licensor may issue a written **Notice of Breach** (delivered by email and recorded in the Platform), specifying the nature of the breach, the action required to rectify it, and the rectification period, which shall be **28 days** from receipt of the notice unless a longer or shorter period is specified in the notice or agreed in writing.

9.2 From the date of the Notice of Breach and until the breach is rectified or the Agreement is terminated, the Nursery's sublicense (or the affected per-cultivar licence, as applicable) is **suspended**. During suspension, the Nursery must not:

  (a) Ship, sell, deliver, or transfer any affected cultivar (including trees already on order);
  (b) Accept new orders for an affected cultivar;
  (c) Collect, propagate, or multiply any propagation material of an affected cultivar,

**without the prior written consent of the Licensor**. The Licensor may grant, withhold, or condition such consent at its absolute discretion.

9.3 If the breach is not rectified within the rectification period (or the Nursery does not demonstrably commence rectification to the Licensor's reasonable satisfaction), the Licensor may **terminate** this Agreement, or the affected per-cultivar licence, by written notice with immediate effect.

9.4 The Licensor may suspend or terminate immediately, without a rectification period, where the breach is:

  (a) Unauthorised propagation, multiplication, or distribution of a sub-licensed cultivar;
  (b) Deliberate mislabelling or substitution;
  (c) Supply to a recipient without an executed Non-Propagation Agreement;
  (d) Insolvency, external administration, or cessation of business of the Nursery; or
  (e) Any other breach that by its nature cannot be rectified.

9.5 On termination (of this Agreement or of a per-cultivar licence), the Nursery must:

  (a) Immediately cease propagation, sale, shipping, and marketing of the affected cultivars;
  (b) Destroy or deliver up (as directed by the Licensor) all unsupplied stock, propagation material, labels, and marketing materials relating to those cultivars;
  (c) Finalise and submit all outstanding disclosures in the Platform; and
  (d) Pay any royalties, program fees, or amounts owing up to the date of termination.

9.6 Clauses 3 (Disclosure), 7 (Audit), 8 (Liability), 9.5 (Obligations on Termination), 10 (Confidentiality), and 12 (General) survive termination.

## 10. Confidentiality

10.1 Each Party must keep confidential all non-public information about the other Party, its business, cultivars, Programs, customers, and pricing, disclosed in connection with this Agreement, and must use such information only for the purposes of this Agreement.

## 11. Records and Data

11.1 The Platform is the authoritative system of record for per-cultivar licences, orders, disclosures, Releases to Ship, and audit logs under this Agreement. The Nursery must keep its Platform records accurate, complete, and up to date.

11.2 The Licensor may retain and process Platform data in accordance with its privacy and data-handling policies for as long as reasonably necessary for audit, compliance, and royalty reconciliation purposes.

## 12. General

12.1 **Assignment:** The Nursery may not assign or subcontract this Agreement or any per-cultivar licence without the Licensor's prior written consent.

12.2 **Variation:** No variation of this Agreement is effective unless recorded in writing and signed by both Parties, or acknowledged through the Platform's variation workflow.

12.3 **Severability:** If any provision is held unenforceable, the remainder of the Agreement continues in effect.

12.4 **Notices:** Notices are validly given when sent by email to the address on record in the Platform for the relevant Party, or by Platform notification.

12.5 **Governing law:** This Agreement is governed by the laws of the jurisdiction of the Region specified above. The Parties submit to the exclusive jurisdiction of the courts of that jurisdiction.

---

## Signatures

**For the Licensor ({{licensor_org}})**

Name: _________________________
Position: _________________________
Signature: _________________________
Date: _________________________

**For the Nursery ({{licensee_org}})**

Name: _________________________
Position: _________________________
Signature: _________________________
Date: _________________________
`;

const NURSERY_SALES_LICENCE_BODY = `# Nursery Sales Licence

**Between**

- **The Licensor:** {{licensor_org}} ("the Licensor"), being the holder or authorised representative of the Plant Breeder's Rights and associated intellectual property in the cultivar named below; and
- **The Nursery:** {{licensee_org}} ("the Nursery").

**Cultivar:** {{cultivar_name}} ({{cultivar_trade_name}}), PBR {{cultivar_pbr_number}}
**Region:** {{region}}
**Term:** {{start_date}} to {{end_date}}
**Trees authorised to sell:** {{max_trees}}

## 1. What this licence grants

1.1 The Licensor authorises the Nursery to take orders for trees of the Cultivar from customers in the Region, **as the Licensor's agent and on the Licensor's behalf**, during the Term.

1.2 This licence grants no right to propagate. It is effective only while the Nursery also holds a current propagation licence from the Licensor for the Cultivar, and ends when that licence ends.

## 2. Orders belong to the Licensor

2.1 Every order the Nursery takes under this licence is an order placed with the Licensor. The Nursery enters it in the Licensor's ordering system and does not issue its own order confirmation, contract, deposit request or invoice for it.

2.2 The Licensor issues the customer's contracts, deposit request and invoices, and the customer pays the Licensor.

2.3 The Nursery must not represent to a customer that it is selling the trees on its own account.

## 3. The Nursery's price and payment

3.1 The Nursery declares to the Licensor its price for each tree of the Cultivar, by tree type. The price declared when an order is placed applies to that order.

3.2 When the customer's deposit is paid, the Licensor pays the Nursery the share of it agreed in the Nursery's propagation licence.

3.3 The Nursery tells the Licensor when the trees are ready, and the freight charge for delivering them, before the Licensor raises its final invoice. The Licensor adds the freight to that invoice.

3.4 On collection or delivery, the Licensor pays the Nursery the balance of its price and the declared freight.

## 4. Records

4.1 The Nursery keeps records of every order taken and every tree supplied under this licence, and gives them to the Licensor on request.

## 5. Ending this licence

5.1 Either party may end this licence by 30 days' written notice. Orders already placed with the Licensor are completed on these terms.

---

*Starting point only. Have it reviewed by a lawyer before use.*

**Signed for the Licensor**
Name: _________________________
Signature: _________________________
Date: _________________________

**Signed for the Nursery**
Name: _________________________
Signature: _________________________
Date: _________________________
`;

export const LICENCE_STARTER_TEMPLATES: LicenceStarterTemplate[] = [
  {
    key: 'nursery-sales-licence',
    name: 'Nursery Sales Licence — Selling on the Licensor\'s Behalf',
    license_tier: 'nursery_sales',
    merge_fields: [
      'licensor_org', 'licensee_org', 'region', 'start_date', 'end_date',
      'cultivar_name', 'cultivar_trade_name', 'cultivar_pbr_number', 'max_trees',
    ],
    body_richtext: NURSERY_SALES_LICENCE_BODY,
    description:
      'Lets a nursery that propagates the cultivar take orders for it on the licensor\'s behalf. The order, contracts, deposit and invoices are the licensor\'s; the nursery declares its tree price and the freight, and is paid its deposit share and the balance by the licensor.',
  },
  {
    key: 'nursery-sublicense-master',
    name: 'Nursery Sublicense — Master Agreement',
    // A master names a group, not a cultivar, and is issued only against a
    // group — so it is a master contract, not a propagation licence.
    license_tier: 'nursery_master_sublicence',
    merge_fields: ['licensor_org', 'licensee_org', 'region', 'start_date', 'end_date'],
    body_richtext: NURSERY_SUBLICENSE_BODY,
    description:
      'Master nursery sublicense covering disclosure, non-propagation, audit, labelling, breach/suspension/termination. Cultivar-specific terms and fees are set per-cultivar / per-Program and are not included here.',
  },
  {
    key: 'orchard-planting-deed',
    name: 'Orchard Planting Deed — Protected Cultivar',
    license_tier: 'orchard_planting',
    merge_fields: [
      'licensor_org', 'licensee_org', 'region', 'start_date', 'end_date',
      'cultivar_name', 'cultivar_trade_name', 'cultivar_protection_status',
      'cultivar_pbr_number', 'cultivar_pbr_grant_date', 'cultivar_pbr_expiry_date',
      'rootstock_name', 'rootstock_protection_status', 'rootstock_pbr_number',
      'rootstock_pbr_expiry_date', 'rootstock_rights_holder',
      'supplying_nursery', 'max_trees', 'tolerance_pct',
      'property_address', 'title_reference', 'graft_union_height_mm',
      'tree_royalty', 'annual_licence_fee', 'production_royalty',
      'rootstock_tree_royalty', 'agreed_sum_per_plant',
      'insurance_cover', 'governing_state',
    ],
    body_richtext: ORCHARD_PLANTING_DEED_BODY,
    description:
      'Grower planting deed for a protected cultivar, built around components rather than a single variety — the scion, rootstock and any interstem are each named, each carries its own rights holder and expiry date, and every prohibition applies per component. Covers the propagation ban (including suckers, stool beds and root pieces below the graft union), graft-union placement, sports and derived varieties, per-component royalties, the annual compliance declaration, accession on sale of the land, and what happens when one component licence ends while another continues.',
  },
  {
    key: 'rootstock-planting-licence',
    name: 'Rootstock Planting Licence — Short Form',
    license_tier: 'orchard_planting',
    merge_fields: [
      'licensor_org', 'licensee_org', 'region', 'start_date', 'end_date',
      'rootstock_name', 'rootstock_pbr_number', 'rootstock_pbr_expiry_date',
      'supplying_nursery', 'max_trees', 'tolerance_pct',
      'property_address', 'graft_union_height_mm',
      'rootstock_tree_royalty', 'agreed_sum_per_plant',
      'insurance_cover', 'governing_state',
    ],
    body_richtext: ROOTSTOCK_PLANTING_LICENCE_BODY,
    description:
      'For the case where the ROOTSTOCK is protected and the scion is not — a heritage or open variety on a protected dwarfing stock. No trade mark, no fruit specification, no production royalty: paid once per tree, and the only real obligation is not to multiply the stock. Re-working to another scion is expressly permitted.',
  },
];
